import PermissionStatusControl from './PermissionStatusControl';
// src/pages/Admin/RoutesPermission/EditPermissionModal.tsx
import React, { useState, useEffect } from 'react';
import { updateRoutePermission, fetchUserLevels } from '../../services/permissionService';
import { ROUTE_PATHS } from '../../routes/routePaths';

interface EditPermissionModalProps {
    isOpen: boolean;
    onClose: () => void;
    onPermissionUpdated: () => void;
    permission: { id: number; path: string; allowed_roles: string[]; activo?: boolean } | null;
}

const EditPermissionModal: React.FC<EditPermissionModalProps> = ({
    isOpen,
    onClose,
    onPermissionUpdated,
    permission,
}) => {
    const [path, setPath] = useState('');
    const [activo, setActivo] = useState(true);
    const [allowedRoles, setAllowedRoles] = useState<string>('');
    const [selectedRole, setSelectedRole] = useState('');
    const [availableRoles, setAvailableRoles] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (permission) {
            setPath(permission.path);
            setActivo(permission.activo !== false);
            setAllowedRoles(permission.allowed_roles.join(', '));
        }
    }, [permission]);

    useEffect(() => {
        const fetchRoles = async () => {
            try {
                const roles = await fetchUserLevels();
                setAvailableRoles(roles);
            } catch (err) {
                /* Browser diagnostics omitted. */
                setError('Error al cargar los roles disponibles');
            }
        };

        if (isOpen) fetchRoles();
    }, [isOpen]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!permission) return;

        setLoading(true);
        setError(null);

        try {
            await updateRoutePermission(permission.id, {
                path,
                allowed_roles: allowedRoles.split(',').map(role => role.trim()).filter(Boolean),
                activo,
            });
            onPermissionUpdated();
            onClose();
        } catch (err) {
            setError('Ocurrió un error al actualizar el permiso.');
            /* Browser diagnostics omitted. */
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen || !permission) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm" />
            <div className="bg-white p-5 sm:p-6 rounded-xl w-full max-w-sm sm:max-w-md mx-auto border border-gray-100 shadow-xl relative max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-start mb-4">
                    <h3 className="text-lg sm:text-xl font-bold text-gray-900">Editar Permiso</h3>
                    <div className="flex items-center gap-2 sm:gap-4">
                        {error && <div className="text-red-500 text-xs sm:text-sm">{error}</div>}
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600 transition-colors p-1"
                            aria-label="Cerrar"
                        >
                            <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Campo Ruta como dropdown */}
                    <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                            Ruta (Path)
                        </label>
                        <select
                            value={path}
                            onChange={(e) => setPath(e.target.value)}
                            className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#c1348b] bg-white"
                            required
                        >
                            <option value="">Selecciona una ruta...</option>
                            {ROUTE_PATHS.map(route => (
                                <option key={route} value={route} className="text-sm sm:text-base">
                                    {route}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Selector de roles */}
                    <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                            Roles Permitidos
                        </label>
                        <select
                            value={selectedRole}
                            onChange={(e) => {
                                const role = e.target.value;
                                setSelectedRole(role);
                                if (role) {
                                    const roles = allowedRoles
                                        .split(',')
                                        .map(r => r.trim())
                                        .filter(r => r !== '');
                                    if (!roles.includes(role)) {
                                        roles.push(role);
                                        setAllowedRoles(roles.join(', '));
                                    }
                                    setSelectedRole('');
                                }
                            }}
                            className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#c1348b] bg-white"
                            disabled={availableRoles.length === 0}
                        >
                            <option value="">Seleccione un rol...</option>
                            {availableRoles.map(role => (
                                <option key={role} value={role} className="text-sm sm:text-base">
                                    {role}
                                </option>
                            ))}
                        </select>
                        <div className="mt-2 flex flex-wrap gap-1 sm:gap-2">
                            {allowedRoles
                                .split(',')
                                .map(r => r.trim())
                                .filter(r => r !== '')
                                .map((role, index) => (
                                    <span
                                        key={index}
                                        className="inline-flex items-center px-2 py-1 bg-[#61257b]/10 text-[#61257b] text-xs sm:text-sm rounded"
                                    >
                                        {role}
                                        <button
                                            type="button"
                                            onClick={() => {
                                                const updatedRoles = allowedRoles
                                                    .split(',')
                                                    .map(r => r.trim())
                                                    .filter(r => r !== role && r !== '')
                                                    .join(', ');
                                                setAllowedRoles(updatedRoles);
                                            }}
                                            className="ml-1 hover:text-[#c1348b] font-bold"
                                        >
                                            ×
                                        </button>
                                    </span>
                                ))}
                        </div>
                    </div>

                    <PermissionStatusControl activo={activo} onChange={setActivo} disabled={loading} />

                    <div className="flex justify-end gap-2 sm:gap-3 mt-6">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-3 sm:px-4 py-2 text-xs sm:text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors font-medium"
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="px-3 sm:px-4 py-2 text-xs sm:text-sm bg-[#c1348b] hover:bg-[#8f1f64] text-white rounded-lg transition-colors font-medium flex items-center"
                        >
                            {loading ? (
                                <span className="flex items-center">
                                    <svg
                                        className="animate-spin h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2"
                                        viewBox="0 0 24 24"
                                    >
                                        <circle
                                            className="opacity-25"
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            stroke="currentColor"
                                            strokeWidth="4"
                                            fill="none"
                                        />
                                        <path
                                            className="opacity-75"
                                            fill="currentColor"
                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                        />
                                    </svg>
                                    <span className="hidden sm:inline">Guardando...</span>
                                    <span className="sm:hidden">...</span>
                                </span>
                            ) : (
                                'Actualizar Permiso'
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EditPermissionModal;
